import { mainSiteUrl } from '@/mocks/categories';
import { seoContent } from '@/mocks/seoData';
import { Link } from 'react-router-dom';

export default function ComparisonCTA() {
  return (
    <>
      {/* Comparison Section */}
      <section id="comparison" className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              {seoContent.comparisonTitle}
            </h2>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-hidden rounded-xl border border-background-200/60">
            <table className="w-full">
              <thead>
                <tr className="bg-background-200/50">
                  {seoContent.comparisonColumns.map((col) => (
                    <th key={col} className="px-6 py-4 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {seoContent.comparisonRows.map((row) => (
                  <tr
                    key={row.platform}
                    className={`border-t border-background-200/40 ${
                      row.highlight ? 'bg-primary-50/50' : 'bg-background-50'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <span className={`text-sm font-semibold ${row.highlight ? 'text-primary-600' : 'text-foreground-800'}`}>
                        {row.platform}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm ${row.highlight ? 'text-primary-600 font-medium' : 'text-foreground-600'}`}>
                        {row.speed}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm ${row.highlight ? 'text-primary-600 font-medium' : 'text-foreground-600'}`}>
                        {row.transparency}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm ${row.highlight ? 'text-primary-600 font-medium' : 'text-foreground-600'}`}>
                        {row.ease}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden flex flex-col gap-4">
            {seoContent.comparisonRows.map((row) => (
              <div
                key={row.platform}
                className={`p-5 rounded-xl border ${
                  row.highlight
                    ? 'bg-primary-50/50 border-primary-200/60'
                    : 'bg-background-50 border-background-200/60'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-semibold ${row.highlight ? 'text-primary-600' : 'text-foreground-800'}`}>
                    {row.platform}
                  </span>
                  {row.highlight && (
                    <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">
                      Recommended
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-foreground-400 mb-1">Speed</p>
                    <p className="text-foreground-700">{row.speed}</p>
                  </div>
                  <div>
                    <p className="text-foreground-400 mb-1">Transparency</p>
                    <p className="text-foreground-700">{row.transparency}</p>
                  </div>
                  <div>
                    <p className="text-foreground-400 mb-1">Ease of Use</p>
                    <p className="text-foreground-700">{row.ease}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20inviting%20fashion%20retail%20concept%20image%20with%20soft%20coral%20and%20cream%20textiles%20draped%20beautifully%20alongside%20modern%20sneakers%20and%20lifestyle%20accessories%20on%20light%20wooden%20surface%2C%20soft%20natural%20daylight%20from%20above%2C%20clean%20editorial%20aesthetic%2C%20gentle%20shadows%2C%20warm%20welcoming%20atmosphere%2C%20high%20end%20product%20photography%2C%20aspirational%20shopping%20lifestyle%20imagery&width=1400&height=500&seq=itaobuy-cta-v3&orientation=landscape"
              alt="ItaobuySpreadsheet shopping system"
              className="w-full h-[300px] md:h-[380px] object-cover rounded-2xl"
              title="Start Using ItaobuySpreadsheet Today"
            />
            <div className="absolute inset-0 bg-black/35 rounded-2xl"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
                {seoContent.ctaTitle}
              </h2>
              <p className="text-sm md:text-base text-white/75 max-w-md mb-8">
                {seoContent.ctaDescription}
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Itaobuy Sheet
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white font-medium text-sm rounded-full border border-white/25 hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Articles Link */}
      <section className="w-full px-6 md:px-10 py-14 bg-background-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-widest mb-3">
              Explore More
            </p>
            <h4 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
              Learn About Spreadsheet Shopping
            </h4>
            <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">
              Discover how the itaobuy spreadsheet system is changing the way people source products globally
            </p>
            <Link
              to="/article/itaobuy-spreadsheet-guide"
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 font-medium text-sm rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
            >
              Read the Complete Guide
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}